<script setup lang="ts">
import { SIDE_NAVIGATION_TOP_LINKS } from "./data.ts";
import { PageLoadingIndicator as IscePageLoadingIndicator } from "@/components/PageLoadingIndicator";
import { SideNavigation as IsceSideNavigation } from "@/components/SideNavigation";
import { useUserStore, useFiltersStore, useUiStore } from "@/store";
import { storeToRefs } from "pinia";
import "@maersk-global/mds-components-core/mc-toast";
import { searchLogic } from "@/components/SearchBar";
import { computed, onMounted, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useRoute } from "vue-router";
import NotificationDrawer from "@/components/NotificationDrawer/NotificationDrawer.vue";
import { useSSE } from "@/compositions/useEventListener";
import { logError, logInfo } from "@/loggers";
import { userAnalyticsService } from "@/services";
import { FEATURE_NAMES } from "@/loggers/types";
import { bypassAuth, CUSTOMER_FEATURE_FLAG_NAME } from "@/global";
import { ScreenBreakpoint, NotificationType } from "@/global/types";
import { useNotificationDrawerStore } from "@/store/NotificationDrawer/NotificationDrawer";
import {
  asyncDownloadFileInBrowser,
  hasJobBeenAutoDownloaded,
  markJobAsAutoDownloaded
} from "@/components/NotificationDrawer/utils/notificationDrawerUtils.ts";
import { AppLayoutV2 } from "@/layout/AppLayoutV2";
import { ENVIRONMENT_VARS } from "@/global/data.ts";
const { resetAppliedFilters } = useFiltersStore();
const uiStore = useUiStore();
const { toast } = storeToRefs(uiStore);
const { initialize } = searchLogic;
const notificationDrawerStore = useNotificationDrawerStore();
const { connectToSSE } = useSSE();
const userStore = useUserStore();
const { isFeatureEnabled, isCustomerFeatureFlagEnabled } = userStore;
const { user } = storeToRefs(userStore);

const route = useRoute();
const { locale, t } = useI18n();

const showSideBar = computed(() => {
  return route.name && route.name !== "Unauthorized" && route.name !== "Uam";
});

const isLoggedIn = computed(() => {
  // Support mock authentication bypass
  if (bypassAuth) {
    return true;
  }

  const tokenExpireTime = user.value?.expires_at;
  if (!tokenExpireTime) {
    return false;
  }
  const currentTime = Math.floor(Date.now() / 1000);
  return tokenExpireTime > currentTime;
});

const updateTextDirection = (lang) => {
  const body = document.body;

  if (lang === "ar") {
    body.classList.add("rtl");
    body.classList.remove("ltr");
  } else {
    body.classList.add("ltr");
    body.classList.remove("rtl");
  }
};

const sideNavigation = computed(() => {
  const links = SIDE_NAVIGATION_TOP_LINKS(
    isFeatureEnabled,
    isCustomerFeatureFlagEnabled,
    userStore.isDndVisibleForBksCustomer
  );
  const isLockedUpsellEnabled = isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.LOCKED_UPSELL_FEATURE_ENABLED);

  return [...links]
    .filter((link) => link.isEnabled !== false || isLockedUpsellEnabled)
    .sort((a, b) => {
      if (a.isEnabled !== false && b.isEnabled === false) return -1;
      if (a.isEnabled === false && b.isEnabled !== false) return 1;
      return 0;
    });
});

const isCollapsibleLayoutEnabled = ENVIRONMENT_VARS.VITE_IS_COLLAPSIBLE_SIDEBAR_ENABLED;

const isAsyncDownloadEnabled = computed(() => isCustomerFeatureFlagEnabled("vite_async_reporting"));
const isAsyncUploadEnabled = computed(() =>
  isCustomerFeatureFlagEnabled(CUSTOMER_FEATURE_FLAG_NAME.ENABLE_ASYNC_UPLOAD)
);

const onNavigationLinkClick = () => {
  resetAppliedFilters();
};

const getScreenBreakpoint = (width: number): ScreenBreakpoint => {
  if (width < 600) return ScreenBreakpoint.MOBILE;
  if (width < 900) return ScreenBreakpoint.TABLET_PORTRAIT;
  if (width < 1200) return ScreenBreakpoint.TABLET_LANDSCAPE;
  if (width < 1800) return ScreenBreakpoint.DESKTOP;
  return ScreenBreakpoint.WIDE_DESKTOP;
};

const sseEventHandler = () => {
  if (!(isAsyncDownloadEnabled.value || isAsyncUploadEnabled.value)) return;

  const featureName = isAsyncDownloadEnabled.value ? FEATURE_NAMES.MVS_ASYNC_DOWNLOAD : FEATURE_NAMES.MVS_ASYNC_UPLOAD;

  connectToSSE((event: any) => {
    const parsedEvent = parseSSEEvent(event, featureName);
    if (!parsedEvent) return;

    if (isRelevantNotification(parsedEvent.type)) {
      handleNotification(parsedEvent);

      if (parsedEvent.type === NotificationType.DOWNLOAD_READY) {
        if (hasJobBeenAutoDownloaded(parsedEvent.jobId)) {
          logInfo(featureName, `Skipping auto-download for jobId ${parsedEvent.jobId} (already auto-downloaded)`);
        } else {
          logInfo(featureName, `Download ready: ${parsedEvent.fileName || "report.xlsx"}`);
          asyncDownloadFileInBrowser(parsedEvent.file, undefined, undefined, parsedEvent.downloadUrl);
          markJobAsAutoDownloaded(parsedEvent.jobId);
        }
      }
    }
  });
};

/** -------------------- Helpers -------------------- **/

const parseSSEEvent = (event: any, featureName: FEATURE_NAMES) => {
  try {
    if (event?.data) return typeof event.data === "string" ? JSON.parse(event.data) : event.data;
    if (typeof event === "string") return JSON.parse(event);
    if (typeof event === "object") return event;
  } catch (e) {
    logError(featureName, `Failed to parse SSE event: ${JSON.stringify(event)} - ${e}`);
    return null;
  }

  logError(featureName, `Unknown event format: ${JSON.stringify(event)}`);
  return null;
};

const isRelevantNotification = (type: NotificationType) => {
  return [
    NotificationType.DOWNLOAD_READY,
    NotificationType.DOWNLOAD_FAILED,
    NotificationType.UPLOAD_FAILED,
    NotificationType.UPLOAD_READY
  ].includes(type);
};

const handleNotification = (parsedEvent: any) => {
  const current = notificationDrawerStore.getNewNotifications();
  notificationDrawerStore.setNewNotifications([...current, parsedEvent]);
};

onMounted(() => {
  sseEventHandler();
  updateTextDirection(locale.value);
  logInfo(FEATURE_NAMES.MVS, "MVS app mounted!");
  const screenWidth = window.innerWidth;
  userAnalyticsService.trackEvent("App: Screen Size", {
    breakpoint: getScreenBreakpoint(screenWidth),
    screenWidth
  });
});

watch(
  () => locale.value,
  (newLocale) => {
    updateTextDirection(newLocale);
  }
);

initialize();
</script>

<template>
  <isce-page-loading-indicator />
  <mc-toast
    v-if="toast"
    :open="toast.open"
    :appearance="toast.appearance"
    :duration="toast.duration ?? 4000"
    position="bottom-center"
    @close="uiStore.clearToast()"
  >
    <mc-notification :body="t(toast.bodyKey)" />
  </mc-toast>
  <template v-if="isLoggedIn">
    <NotificationDrawer />
    <!-- AppLayout V2 (feature flag ON) -->
    <template v-if="isCollapsibleLayoutEnabled">
      <app-layout-v2
        v-if="showSideBar"
        :links="sideNavigation"
        data-test="app-view/layout-v2"
        @report-problem-click="() => {}"
      >
        <router-view />
      </app-layout-v2>
      <router-view v-if="!showSideBar" />
    </template>

    <!-- Classic layout (feature flag OFF) — unchanged -->
    <template v-else>
      <div v-if="showSideBar" class="layout-container" data-test="app-view/layout">
        <div class="layout-container__side-bar">
          <isce-side-navigation :top-links="sideNavigation" @navigation-link-click="onNavigationLinkClick" />
        </div>
        <div class="layout-container__content">
          <router-view />
        </div>
      </div>
      <router-view v-if="!showSideBar" />
    </template>
  </template>
</template>

<style lang="scss" scoped>
.layout-container {
  display: grid;
  grid-template-columns: 248px auto;

  &__side-bar {
    overflow: auto;
    grid-row: 1/2;
    position: relative;
    z-index: 1;
  }

  &__content {
    overflow: hidden;
    height: 100vh;
  }
}
</style>
